import { DynMapMetadata } from "@faro-lotv/service-wires";

/** The tile grid parameters of a dynamic map */
export type DynMapGrid = Pick<
  DynMapMetadata,
  "tileSizePx" | "tileSizeM" | "levels"
>;

/** Coordinates of a tile, y grows north */
export type TileKey = { level: number; x: number; y: number };

/** An axis aligned rectangle in the dynamic map (Z-up world) XY plane */
export type Rect2 = { minX: number; minY: number; maxX: number; maxY: number };

/**
 * @returns the edge length in meters of the tiles at the given level
 * @param grid of the map
 * @param level of detail, 0 is the finest
 */
export function tileSizeMeters(grid: DynMapGrid, level: number): number {
  return grid.tileSizeM * 2 ** level;
}

/**
 * @returns the half-open world rectangle covered by a tile
 * @param grid of the map
 * @param key of the tile
 */
export function tileWorldRect(grid: DynMapGrid, key: TileKey): Rect2 {
  const size = tileSizeMeters(grid, key.level);
  return {
    minX: key.x * size,
    minY: key.y * size,
    maxX: (key.x + 1) * size,
    maxY: (key.y + 1) * size,
  };
}

/**
 * @returns the coarsest level whose texels are not larger than a device pixel
 * @param grid of the map
 * @param metersPerDevicePixel world size of a device pixel at the current view
 * @param bias added to the computed level, positive values trade sharpness for fewer requests
 */
export function selectLevel(
  grid: DynMapGrid,
  metersPerDevicePixel: number,
  bias = 0,
): number {
  const level =
    Math.floor(
      Math.log2((metersPerDevicePixel * grid.tileSizePx) / grid.tileSizeM),
    ) + bias;
  return Math.min(Math.max(level, 0), grid.levels - 1);
}

/**
 * @returns the tiles of a level intersecting a world rectangle
 * @param grid of the map
 * @param level of the tiles
 * @param rect the visible world rectangle
 */
export function visibleTiles(
  grid: DynMapGrid,
  level: number,
  rect: Rect2,
): TileKey[] {
  const size = tileSizeMeters(grid, level);
  const tiles: TileKey[] = [];
  const maxX = Math.ceil(rect.maxX / size) - 1;
  const maxY = Math.ceil(rect.maxY / size) - 1;
  for (let y = Math.floor(rect.minY / size); y <= maxY; ++y) {
    for (let x = Math.floor(rect.minX / size); x <= maxX; ++x) {
      tiles.push({ level, x, y });
    }
  }
  return tiles;
}

/**
 * @returns the number of tiles of a level intersecting a world rectangle
 * @param grid of the map
 * @param level of the tiles
 * @param rect the visible world rectangle
 */
export function countVisibleTiles(
  grid: DynMapGrid,
  level: number,
  rect: Rect2,
): number {
  const size = tileSizeMeters(grid, level);
  const nx = Math.ceil(rect.maxX / size) - Math.floor(rect.minX / size);
  const ny = Math.ceil(rect.maxY / size) - Math.floor(rect.minY / size);
  return Math.max(nx, 0) * Math.max(ny, 0);
}

/**
 * @returns the parent tile, one level coarser
 * @param key of the child tile
 */
export function parentOf(key: TileKey): TileKey {
  return {
    level: key.level + 1,
    x: Math.floor(key.x / 2),
    y: Math.floor(key.y / 2),
  };
}

/**
 * @returns the compact representation of a set of scan indices (e.g. "0-2,5,7-9"), undefined if empty
 * @param indices of the scans in the map metadata
 */
export function encodeScanList(indices: Iterable<number>): string | undefined {
  const sorted = [...new Set(indices)].sort((a, b) => a - b);
  const runs: string[] = [];
  for (let i = 0; i < sorted.length; ++i) {
    const start = sorted[i];
    while (sorted[i + 1] === sorted[i] + 1) ++i;
    runs.push(start === sorted[i] ? `${start}` : `${start}-${sorted[i]}`);
  }
  return runs.length ? runs.join(",") : undefined;
}
